import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react"
import {
  deepCopy,
  logger,
  lowercaseFirstCharacter,
  Status,
} from "../services/utilities/utility"
import { MainContext } from "./main"
import { useListController } from "../hooks/useListController"
import { useLazyQuery, useMutation } from "@apollo/client"
import {
  checkinGroupGet,
  checkinGroupList,
  checkinListByGroupId,
} from "../services/graphql/queries"
import Document from "../models/document"
import CheckInGroup from "../models/checkInGroup"
import CheckInGroupsDocument from "../models/checkInGroupsDocument"
import {
  checkinGroupDelete,
  checkinGroupInsert,
  checkinGroupUpdate,
  upsertDocumentCheckInGroup,
} from "../services/graphql/mutations"
import CheckIn from "../models/checkIn"

interface CheckInGroupsContextInterface {
  loading: boolean
  setLoading: Dispatch<SetStateAction<boolean>>
  checkInGroupsList: CheckInGroup[]
  getCheckInGroupsList: (withLoading?: boolean) => void
  hasSearched: boolean
  getCurrentCheckInGroup: (checkInGroupId: string) => Promise<boolean>
  currentCheckInGroup: CheckInGroup | null
  setCurrentCheckInGroup: Dispatch<SetStateAction<CheckInGroup | null>>
  preChangesCurrentCheckInGroup: CheckInGroup | null
  currentCheckInGroupCheckInsList: CheckIn[]
  setCurrentCheckInGroupCheckInsList: Dispatch<SetStateAction<CheckIn[]>>
  doneChanges: boolean
  cancelChanges: () => void
  updatingList: boolean
  setUpdatingList: Dispatch<SetStateAction<boolean>>
  checkInGroupsListNextToken: string | null
  loadMoreCheckInGroups: () => Promise<boolean>
  editMode: boolean
  setEditMode: Dispatch<SetStateAction<boolean>>
  addTranslation: (translationToAdd: string) => void
  hasError: boolean
  endDateError: boolean
  setEndDateError: Dispatch<SetStateAction<boolean>>
  translationsErrors: { lang: string; hasErrors: boolean }[]
  setTranslationsErrors: Dispatch<
    SetStateAction<{ lang: string; hasErrors: boolean }[]>
  >
  copyDetailsFromDefault: (itemToCopyToIndex: number) => void
  copyBodyFromDefault: (itemToCopyToIndex: number) => void
  createCheckInGroup: (input: {
    startDate: string
    endDate: string
    projectId: string
    teamId?: string
  }) => Promise<string | boolean>
  createCheckInGroupDocument: (input: {
    parentId: string
    type: string
    checkinGroupDocumentItems: {
      body: object[]
      default: boolean
      lang: string
      title: string
      description: string
    }[]
  }) => Promise<boolean>
  upsertCheckInGroupDocument: () => Promise<boolean>
  deleteCheckInGroup: (id: string) => Promise<boolean>
  updateCheckInGroup: () => Promise<boolean>
}

const CheckInGroupsContext = createContext<CheckInGroupsContextInterface>({
  loading: true,
  setLoading: () => {},
  checkInGroupsList: [],
  getCheckInGroupsList: () => {},
  hasSearched: false,
  getCurrentCheckInGroup: async () => true,
  currentCheckInGroup: null,
  setCurrentCheckInGroup: () => {},
  preChangesCurrentCheckInGroup: null,
  currentCheckInGroupCheckInsList: [],
  setCurrentCheckInGroupCheckInsList: () => {},
  doneChanges: false,
  cancelChanges: () => {},
  updatingList: false,
  setUpdatingList: () => {},
  checkInGroupsListNextToken: null,
  loadMoreCheckInGroups: async () => true,
  editMode: true,
  setEditMode: () => {},
  addTranslation: () => {},
  hasError: false,
  endDateError: false,
  setEndDateError: () => {},
  translationsErrors: [],
  setTranslationsErrors: () => {},
  copyDetailsFromDefault: () => {},
  copyBodyFromDefault: () => {},
  createCheckInGroup: async () => false,
  createCheckInGroupDocument: async () => true,
  upsertCheckInGroupDocument: async () => true,
  deleteCheckInGroup: async () => true,
  updateCheckInGroup: async () => true,
})

const CheckInGroupsController = ({ children }: { children: ReactNode }) => {
  const { setError, setErrorMessage, updatePanelStatus } =
    useContext(MainContext)

  // states
  const [currentCheckInGroup, setCurrentCheckInGroup] =
    useState<CheckInGroup | null>(null) // current checkin group details
  const [preChangesCurrentCheckInGroup, setPreChangesCurrentCheckInGroup] =
    useState<CheckInGroup | null>(null) // fetched current checkin group details
  const [currentCheckInGroupCheckInsList, setCurrentCheckInGroupCheckInsList] =
    useState<CheckIn[]>([]) // current checkin group checkins list
  const [doneChanges, setDoneChanges] = useState<boolean>(false) // if user has done changes to checkin group details
  const [editMode, setEditMode] = useState<boolean>(true) // if is edit mode or not

  // errors for checkin edit
  const [endDateError, setEndDateError] = useState<boolean>(false)
  const [translationsErrors, setTranslationsErrors] = useState<
    { lang: string; hasErrors: boolean }[]
  >([]) // errors array for translations
  const hasError: boolean =
    endDateError ||
    translationsErrors.filter((item) => item.hasErrors).length !== 0 // if there are errors or not

  // reset all errors
  const resetErrors = () => {
    setEndDateError(false)
    const newTranslationsErrors: { lang: string; hasErrors: boolean }[] = []
    currentCheckInGroup.document.items.forEach((item) => {
      newTranslationsErrors.push({ lang: item.lang, hasErrors: false })
    })
    setTranslationsErrors(newTranslationsErrors)
  }

  // queries
  const [checkinGroupListQuery] = useLazyQuery(checkinGroupList)
  const [checkinListByGroupIdQuery] = useLazyQuery(checkinListByGroupId)
  const [checkinGroupGetQuery] = useLazyQuery(checkinGroupGet)

  // mutations
  const [checkinGroupInsertMutation] = useMutation(checkinGroupInsert)
  const [upsertDocumentCheckInGroupMutation] = useMutation(
    upsertDocumentCheckInGroup
  )
  const [checkinGroupDeleteMutation] = useMutation(checkinGroupDelete)
  const [checkinGroupUpdateMutation] = useMutation(checkinGroupUpdate)

  // list state + fetching (loading, pagination, panelStatus, error handling)
  const {
    list: checkInGroupsList,
    setList: setCheckInGroupsList,
    loading,
    setLoading,
    updatingList,
    setUpdatingList,
    nextToken: checkInGroupsListNextToken,
    setNextToken: setCheckInGroupsListNextToken,
    hasSearched,
    setHasSearched,
    getList: getCheckInGroupsList,
    loadMore: loadMoreCheckInGroups,
  } = useListController<CheckInGroup>({
    panelName: "Check-in Groups",
    logLabel: "checkinGroupList",
    queryDocument: checkinGroupList,
    fetchPage: ({ limit, nextToken }) =>
      checkinGroupListQuery({
        variables: {
          input: {
            limit: limit ?? Math.round(window.innerHeight / 74) + 10,
            ...(nextToken !== undefined ? { nextToken } : {}),
          },
        },
      }),
    selectResult: (data) => ({
      items: data.checkinGroupList.items,
      nextToken: data.checkinGroupList.nextToken,
    }),
  })

  // get checkin group details
  const getCurrentCheckInGroup = useCallback(
    async (checkInGroupId: string) => {
      try {
        logger(Status.Api, "QUERY checkinGroupGet", checkinGroupGet)
        logger(Status.Api, "QUERY checkinListByGroupId", checkinListByGroupId)
        const result = await Promise.all([
          checkinGroupGetQuery({
            variables: { input: { id: checkInGroupId } },
          }),
          checkinListByGroupIdQuery({
            variables: { input: { groupId: checkInGroupId, limit: 100 } },
          }),
        ])
        logger(
          Status.Info,
          `checkIn group ${checkInGroupId}`,
          result[0].data.checkinGroupGet
        )
        logger(
          Status.Info,
          "checkIn group checkIns list",
          result[1].data.checkinListByGroupId.items
        )

        setCurrentCheckInGroup(result[0].data.checkinGroupGet)
        setPreChangesCurrentCheckInGroup(
          deepCopy(result[0].data.checkinGroupGet)
        )
        result[0].data.checkinGroupGet.document.items.forEach(
          (item: Document) => {
            translationsErrors.push({ lang: item.lang, hasErrors: false })
          }
        )
        setTranslationsErrors([...translationsErrors])

        setCurrentCheckInGroupCheckInsList(
          result[1].data.checkinListByGroupId.items
        )

        return true
      } catch (e: unknown) {
        if (e instanceof Error) {
          setError(true)
          setErrorMessage(e.message)
          logger(Status.Error, `checkinGroupGet`, e.message)
        }
        return false
      }
    },
    [setError, setErrorMessage]
  )

  // update checkin group
  const updateCheckInGroup = async () => {
    try {
      const input: { id: string; startDate: string; endDate: string } = {
        id: currentCheckInGroup.id,
        startDate: currentCheckInGroup.startDate,
        endDate: currentCheckInGroup.endDate,
      }

      logger(Status.Api, "MUTATION checkinGroupUpdate", checkinGroupUpdate)
      const { data } = await checkinGroupUpdateMutation({
        variables: { input },
      })
      logger(Status.Info, `checkIn group updated`, data.checkinGroupUpdate)

      setCurrentCheckInGroup((current) => ({
        ...current,
        ...data.checkinGroupUpdate,
      }))
      setPreChangesCurrentCheckInGroup((current) => ({
        ...current,
        ...deepCopy(data.checkinGroupUpdate),
      }))

      return true
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(true)
        setErrorMessage(e.message)
        logger(Status.Error, `checkinGroupUpdate`, e.message)
      }
      return false
    }
  }

  // upsert checkin group document
  const upsertCheckInGroupDocument = async () => {
    try {
      // parse data for inpout
      const currentCheckInGroupCopy = deepCopy(currentCheckInGroup)
      currentCheckInGroupCopy.document.items.forEach((item: any) => {
        const newBody = []
        if (item.body) {
          item.body.forEach((bodyItem: any) => {
            const { sliceType, __typename, ...rest } = bodyItem
            newBody.push({
              [lowercaseFirstCharacter(bodyItem.sliceType)]: {
                ...rest,
              },
            })
          })
        }
        item.body = newBody

        item.default = item.isDefault
        delete item.isDefault
        delete item.type
        delete item.updatedAt
        delete item.parentId
        delete item.__typename
      })

      const input = {
        checkinGroupDocumentItems: currentCheckInGroupCopy.document.items,
        parentId: currentCheckInGroupCopy.document.parentId,
        type: "CheckinGroup",
      }

      logger(Status.Api, "MUTATION documentUpsert", upsertDocumentCheckInGroup)
      const { data } = await upsertDocumentCheckInGroupMutation({
        variables: { input: input },
      })
      logger(
        Status.Info,
        `checkIn group ${data.documentUpsert.parentId} document upserted`,
        data.documentUpsert
      )

      setCurrentCheckInGroup((current) => {
        return {
          ...current,
          document: data.documentUpsert,
        }
      })
      setPreChangesCurrentCheckInGroup((current) => {
        return {
          ...current,
          document: deepCopy(data.documentUpsert),
        }
      })

      return true
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(true)
        setErrorMessage(e.message)
        logger(Status.Error, `documentUpsert`, e.message)
      }
      return false
    }
  }

  // create checkin group
  const createCheckInGroup = async (input: {
    startDate: string
    endDate: string
    projectId: string
    teamId?: string
  }) => {
    try {
      logger(Status.Api, "MUTATION checkinGroupInsert", checkinGroupInsert)
      const { data } = await checkinGroupInsertMutation({
        variables: { input: input },
      })
      logger(Status.Info, `checkIn group created`, data.checkinGroupInsert)

      return data.checkinGroupInsert.id
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(true)
        setErrorMessage(e.message)
        logger(Status.Error, `checkinGroupInsert`, e.message)
      }
      return false
    }
  }

  // create checkin group document
  const createCheckInGroupDocument = async (input: {
    parentId: string
    type: string
    checkinGroupDocumentItems: {
      body: object[]
      default: boolean
      lang: string
      title: string
      description: string
    }[]
  }) => {
    try {
      logger(Status.Api, "MUTATION documentUpsert", upsertDocumentCheckInGroup)
      const { data } = await upsertDocumentCheckInGroupMutation({
        variables: { input: input },
      })
      logger(Status.Info, `document created`, data.documentUpsert)

      return true
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(true)
        setErrorMessage(e.message)
        logger(Status.Error, `documentUpsert`, e.message)
      }
      return false
    }
  }

  // delete checkin group
  const deleteCheckInGroup = async (id: string) => {
    try {
      logger(Status.Api, "MUTATION checkinGroupDelete", checkinGroupDelete)
      await checkinGroupDeleteMutation({
        variables: {
          input: {
            id,
          },
        },
      })
      logger(Status.Info, `checkIn group ${id} deleted`)

      return true
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(true)
        setErrorMessage(e.message)
        logger(Status.Error, `checkinGroupDelete`, e.message)
      }
      return false
    }
  }

  // add translation
  const addTranslation = (translationToAdd: string) => {
    const documentToAdd: CheckInGroupsDocument = {
      body: [],
      isDefault: false,
      lang: translationToAdd,
      title: "Title",
      description: "Description",
      type: "CheckinGroup",
    }

    translationsErrors.push({
      lang: translationToAdd,
      hasErrors: false,
    })
    setTranslationsErrors([...translationsErrors])

    currentCheckInGroup.document.items.push(documentToAdd)
    setCurrentCheckInGroup({ ...currentCheckInGroup })
  }

  // copy details from default translation
  const copyDetailsFromDefault = (itemToCopyToIndex: number) => {
    const defaultItem = currentCheckInGroup.document.items.find(
      (item) => item.isDefault
    )

    currentCheckInGroup.document.items[itemToCopyToIndex].title =
      defaultItem.title
    currentCheckInGroup.document.items[itemToCopyToIndex].description =
      defaultItem.description
    currentCheckInGroup.document.items[itemToCopyToIndex].body =
      defaultItem.body

    setCurrentCheckInGroup({ ...currentCheckInGroup })
  }

  // copy body from default translation
  const copyBodyFromDefault = (itemToCopyToIndex: number) => {
    const defaultItem = currentCheckInGroup.document.items.find(
      (item) => item.isDefault
    )

    currentCheckInGroup.document.items[itemToCopyToIndex].body =
      defaultItem.body

    setCurrentCheckInGroup({ ...currentCheckInGroup })
  }

  // check if user has done changes to details
  useEffect(() => {
    if (currentCheckInGroup && preChangesCurrentCheckInGroup) {
      const currentCheckInGroupCopy: CheckInGroup =
        deepCopy(currentCheckInGroup)
      const preChangesCurrentCheckInGroupCopy: CheckInGroup = deepCopy(
        preChangesCurrentCheckInGroup
      )

      if (
        JSON.stringify(currentCheckInGroupCopy) ===
        JSON.stringify(preChangesCurrentCheckInGroupCopy)
      ) {
        setDoneChanges(false)
      } else {
        setDoneChanges(true)
      }
    }
  }, [currentCheckInGroup, preChangesCurrentCheckInGroup])

  // cancel details changes
  const cancelChanges = () => {
    setCurrentCheckInGroup(deepCopy(preChangesCurrentCheckInGroup))
    resetErrors()
  }

  // add update function to panel status
  useEffect(() => {
    updatePanelStatus("Check-in Groups", {
      updateFunction: getCheckInGroupsList,
    })
  }, [])

  return (
    <CheckInGroupsContext.Provider
      value={{
        loading,
        setLoading,
        checkInGroupsList,
        getCheckInGroupsList,
        hasSearched,
        getCurrentCheckInGroup,
        currentCheckInGroup,
        setCurrentCheckInGroup,
        preChangesCurrentCheckInGroup,
        currentCheckInGroupCheckInsList,
        setCurrentCheckInGroupCheckInsList,
        doneChanges,
        cancelChanges,
        updatingList,
        setUpdatingList,
        checkInGroupsListNextToken,
        loadMoreCheckInGroups,
        editMode,
        setEditMode,
        addTranslation,
        hasError,
        endDateError,
        setEndDateError,
        translationsErrors,
        setTranslationsErrors,
        copyDetailsFromDefault,
        copyBodyFromDefault,
        createCheckInGroup,
        createCheckInGroupDocument,
        upsertCheckInGroupDocument,
        deleteCheckInGroup,
        updateCheckInGroup,
      }}
    >
      {children}
    </CheckInGroupsContext.Provider>
  )
}

export { CheckInGroupsController, CheckInGroupsContext }
